import { Component, PLATFORM_ID, Renderer2, ViewEncapsulation, inject, OnInit, OnDestroy } from '@angular/core';
import { MetaService } from './shared/services/meta_service';
import { UserService } from './shared/services/user_service';
import { A2hsService } from './shared/services/a2hs.service';
import { GtmService } from './shared/services/gtm_service';
import { ThemeService } from './shared/services/theme_service';
import { AnalyticsService } from './shared/services/analytics_service';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs';
import { ConfirmationModalComponent } from './shared/confirmation-modal/confirmation-modal.component';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Util } from './shared/util/util';
import { NavigationEnd, Router, ActivatedRoute, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { AdBlockCheckerComponent } from './shared/adblock-checker/adblock-checker.component';
import { CookieConsentComponent } from './shared/cookieconsent/cookie-consent.component';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: Array<string>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    RouterOutlet,
    ConfirmationModalComponent,
    AdBlockCheckerComponent,
    CookieConsentComponent,
    CommonModule
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  public a2hsService = inject(A2hsService);
  private metaService = inject(MetaService);
  private userService = inject(UserService);
  private modalService = inject(NgbModal);
  private utilities = inject(Util);
  private renderer = inject(Renderer2);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private gtmService = inject(GtmService);
  private analyticsService = inject(AnalyticsService);
  protected platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private themeService = inject(ThemeService);

  currentLoginState!: boolean;
  public isDesktop = false;
  private userLoggedSubscription!: Subscription;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const html = this.document.querySelector('html');
      this.renderer.addClass(html, this.isDesktop ? 'device--desktop' : 'device--mobile');
    }
  }

  public ngOnInit(): void {
    this.userLoggedSubscription = this.userService.curUser$.subscribe(() => {
      this.currentLoginState = this.userService.isLoggedIn();
    });

    if (isPlatformBrowser(this.platformId)) {
      this.isDesktop = !this.utilities.isTouchDevice();

      window.addEventListener('focus', this.checkLoginOnTabFocus.bind(this));
      window.addEventListener('blur', this.checkLoginOnTabFocus.bind(this));

      this.a2hsService.checkUserAgent();
      this.a2hsService.trackStandalone();

      window.addEventListener('beforeinstallprompt', (e: BeforeInstallPromptEvent) => {
        e.preventDefault();
        this.a2hsService.promptIntercepted = true;
        this.a2hsService.deferredPrompt = e;
        this.a2hsService.promptSaved = true;
      });
      window.addEventListener('appinstalled', () => {
        this.a2hsService.trackInstalled();
      });

      this.router.events.forEach(item => {
        if (item instanceof NavigationEnd) {
          const gtmTag = {
            event: 'page',
            pageName: item.url
          };

          this.gtmService.pushTag(gtmTag);
        }
      });

      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      )
        .subscribe(() => {
          const urlParams = this.route.snapshot.queryParams;
          const filteredParams: { [key: string]: string | undefined } = {};

          for (const [key, value] of Object.entries(urlParams)) {
            if (typeof value === 'string' && key.toLowerCase().includes('utm_')) {
              filteredParams[key] = value;
            }
          }

          if (Object.keys(filteredParams).length > 0) {
            this.analyticsService.sendUtmTags(filteredParams as { [key: string]: string });
          }
        });
    }
  }

  ngOnDestroy() {
    if (this.userLoggedSubscription) {
      this.userLoggedSubscription.unsubscribe();
    }

    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('focus', this.checkLoginOnTabFocus.bind(this));
      window.removeEventListener('blur', this.checkLoginOnTabFocus.bind(this));
    }
  }

  checkLoginOnTabFocus(e: Event) {
    if (e.type === 'blur') {
      // console.log('you are outside the tab');
    }

    if (this.currentLoginState) {
      if (e.type === 'focus') {
        this.userService.getProfile(true).then(user => {
          if (user.id === '') {
            this.userService.logout().then(() => {
              this.showModalMessage(
                'Session timeout',
                'Your session expired. Please try to login again.'
              );
            });
          }
        }, err => {
          console.error(err.error || err);
          this.userService.logout().then(() => {
            this.showModalMessage(
              'Session timeout',
              'Your session expired. Please try to login again.'
            );
          });
        });
      }
    }
  }

  showModalMessage(title: string, message: string) {
    const modalOptions: NgbModalOptions = {};
    const refModalConf = this.modalService.open(ConfirmationModalComponent, modalOptions);

    refModalConf.componentInstance.onlyMessage = true;
    refModalConf.componentInstance.confirmationModalTitle = title;
    refModalConf.componentInstance.confirmationModalMsg = message;

    refModalConf.result.then(resp => {
      if (resp) {
        console.log(resp);
      }
    })
      .catch(err => {
        console.log(err);
      });
  }
}